<vw-state [state]="state" titleKey="garden.overview.load-error" (retry)="retry()">
  <div skeleton class="grid grid-cols-1 gap-4">
    @for (i of [1, 2]; track i) {
      <vw-skeleton-loader [width]="'100%'" [height]="'88px'" [borderRadius]="'1rem'"></vw-skeleton-loader>
    }
  </div>
</vw-state>
@if (state.status === 'ready') {
  <div class="flex flex-col gap-5">
    <!-- Réseau coupé : le potager du dernier chargement, daté (NA-05) -->
    <vw-offline-banner (retry)="retry()"></vw-offline-banner>
    <vw-season-plan-banner></vw-season-plan-banner>
    <!-- Zone d'office « ZONE 1 » en intérieur qui porte des plantes : on fait confirmer,
         l'intérieur coupe les alertes de gel. -->
    @if (indoorCheck) {
      <vw-action-needed-banner
        [message]="'garden.overview.indoor-check.message' | translate: { zone: (indoorCheck.view.zone | vwZoneName), plants: indoorCheck.plantNames }"
        [confirmLabel]="'garden.overview.indoor-check.outdoor' | translate"
        [dismissLabel]="'garden.overview.indoor-check.indoor' | translate"
        (confirmed)="declareOutdoor()"
        (dismissed)="confirmIndoor()"></vw-action-needed-banner>
    }

    @for (z of zones; track z.zone.uid) {
      <section class="flex flex-col gap-3">
        <!-- En-tête de zone : identique que la zone contienne des planches/cultures ou non,
             pour qu'une zone vide reste visuellement une zone (et pas une planche). -->
        <div class="flex items-center justify-between gap-2">
          <div vwPressable class="flex min-w-0 items-center gap-2 cursor-pointer" (click)="openZoneEdit(z)">
            <span aria-hidden="true" class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vw-surface-tint text-base">{{ zoneEmoji(z.zone.locationType) }}</span>
            <div class="min-w-0">
              <h3 class="truncate text-lg font-semibold leading-tight text-vw-ink">{{ z.zone | vwZoneName }}</h3>
              <p class="truncate text-[11px] leading-tight text-vw-muted">
                @if (z.zone | vwHasCustomZoneName) {
                  {{ 'profile.addresses.edit-address.edit-locations.locations.' + z.zone.locationType | translate }},
                }
                @if (z.zone.sunchineLevels) {
                  {{ 'garden.beds.enums.exposure.' + z.zone.sunchineLevels | translate }},
                }
                {{ 'garden.overview.beds-count' | vwPlural: z.beds.length }}
              </p>
            </div>
          </div>
          @if (z.locked) {
            <!-- Consultable : rien n'a été supprimé, les cultures en cours
                 restent gérables. Le mot compte — « verrouillé » ferait croire
                 à une perte. -->
            <span class="shrink-0 rounded-full bg-grey-100 px-2 py-0.5 text-[11px] text-vw-muted">
              🔒 {{ 'garden.overview.consultable' | translate }}
            </span>
          } @else {
          <!-- À la limite, le « + » prend la teinte premium : il n'échoue pas,
               il ouvre l'offre. Le signal est discret — un cadenas franc
               ressemblerait à une porte fermée. -->
          <button type="button" (click)="addBedToZone(z.zone)"
            class="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-white text-lg leading-none active:bg-primary-50"
            [class]="
              bedLimitReached(z)
                ? 'border-vw-premium-line text-[#B36A00]'
                : 'border-gray-200 text-vw-muted active:text-primary-600'
            "
            [attr.aria-label]="(bedLimitReached(z)
              ? ('premium.teaser.beds' | translate)
              : ('garden.overview.add-bed.title' | translate) + ', ' + z.zone.name)">
            +
            @if (bedLimitReached(z)) {
              <span class="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-vw-premium-accent"></span>
            }
          </button>
          }
        </div>

        @if (z.locked) {
          <!-- Bandeau ambré : dire ce qui reste possible, pas ce qui est perdu -->
          <div class="flex items-start gap-2.5 rounded-xl border border-vw-premium-line bg-[#FFF9EC] px-3 py-2.5">
            <span class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-vw-premium-accent text-white">
              <vw-lock-icon class="h-3.5 w-3.5" />
            </span>
            <p class="text-xs leading-relaxed text-vw-premium-ink">
              {{ 'garden.overview.locked-explain' | translate }}
            </p>
          </div>
        }

        <!-- Planches en lignes compactes -->
        @if (z.beds.length) {
          <div class="divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-lg">
            @for (b of z.beds; track b.bed.uid) {
              <div vwPressable (click)="openBed(b.bed)" class="cursor-pointer active:bg-primary-50">
                <vw-bed-item [bed]="b.bed" [cultures]="b.cultures" [count]="b.count" [plantCount]="b.plantCount" [freeCells]="b.freeCells" [totalCells]="b.totalCells" [occupied]="b.occupied" [actionNeeded]="b.actionNeeded"></vw-bed-item>
              </div>
            }
          </div>
        }

        @if (z.locked) {
          <button type="button" (click)="openLockedStructurePaywall()"
            class="flex w-full items-center justify-center gap-2 rounded-xl border border-vw-premium-line bg-[#FFF9EC] px-3 py-2.5 text-sm font-medium text-vw-premium-ink">
            <vw-lock-icon class="h-4 w-4" />
            {{ 'garden.overview.add-plant-here' | translate }}
          </button>
        }

        <!-- Cultures de la zone non placées dans une planche (rattachées à la zone) -->
        @if (z.looseCultures.length) {
          <div class="rounded-2xl bg-white p-3 shadow-lg">
            <p class="mb-2 text-[11px] font-medium uppercase tracking-wide text-vw-muted">🌱 {{ 'garden.overview.off-bed' | translate }}</p>
            <div class="grid grid-cols-3 gap-3">
              @for (g of z.looseCultures; track $index) {
                <div vwPressable (click)="openCulture(g)" class="cursor-pointer">
                  <div class="relative aspect-square w-full overflow-hidden rounded-lg">
                    <vw-cultivation-item-viewer [cultivation]="$any(g.culture)" displayType="SMALL" [showName]="false" class="block h-full w-full"></vw-cultivation-item-viewer>
                    @if (g.actionNeeded) {
                      <vw-action-badge class="absolute left-1 top-1" [ariaLabel]="'garden.overview.action-todo' | translate"></vw-action-badge>
                    }
                    @if (g.plantCount > 1) {
                      <span class="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-white/95 px-1.5 py-0.5 text-[11px] font-bold leading-none text-vw-ink shadow-sm">
                        <img src="/assets/svg/leaf.svg" alt="" class="h-3.5 w-3.5" />{{ g.plantCount }}
                      </span>
                    }
                  </div>
                  <p class="mt-1.5 truncate text-center text-sm font-semibold text-vw-ink">{{ $any(g.culture)?.name }}</p>
                </div>
              }
            </div>
          </div>
        }
      </section>
    }

    <!-- Cultures sans zone (orphelines) uniquement — celles rattachées à une zone restent sous leur zone. -->
    @if (orphanCultures.length) {
      <section class="flex flex-col gap-2 border-t border-gray-100 pt-4">
        <p class="text-xs font-semibold uppercase tracking-wide text-vw-muted">{{ 'garden.overview.no-zone' | translate }}</p>
        <div class="grid grid-cols-3 gap-3">
          @for (g of orphanCultures; track $index) {
            <div vwPressable (click)="openCulture(g)" class="cursor-pointer">
              <div class="relative aspect-square w-full overflow-hidden rounded-lg">
                <vw-cultivation-item-viewer [cultivation]="$any(g.culture)" displayType="SMALL" [showName]="false" class="block h-full w-full"></vw-cultivation-item-viewer>
                @if (g.actionNeeded) {
                  <vw-action-badge class="absolute left-1 top-1" [ariaLabel]="'garden.overview.action-todo' | translate"></vw-action-badge>
                }
                @if (g.plantCount > 1) {
                  <span class="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-white/95 px-1.5 py-0.5 text-[11px] font-bold leading-none text-vw-ink shadow-sm">
                    <img src="/assets/svg/leaf.svg" alt="" class="h-3.5 w-3.5" />{{ g.plantCount }}
                  </span>
                }
              </div>
              <p class="mt-1.5 truncate text-center text-sm font-semibold text-vw-ink">{{ $any(g.culture)?.name }}</p>
            </div>
          }
        </div>
      </section>
    }

    @if (!zones.length && !orphanCultures.length) {
      <!-- L'écran de toute personne qui vient de s'inscrire : deux portes d'entrée. -->
      <vw-empty-state willy="hi" titleKey="garden.overview.empty" messageKey="garden.overview.empty-text"
        actionKey="garden.overview.empty-plant" actionLook="primary"
        secondaryActionKey="garden.overview.empty-zone"
        (action)="plantFirst()" (secondaryAction)="addFirstZone()"></vw-empty-state>
    }

    <!-- En bas de l'inventaire, une fois la limite atteinte : l'utilisateur
         voit son potager complet et apprend qu'il peut l'agrandir. Avant la
         limite, l'encart n'aurait rien à débloquer. -->
    @if (structureLimitHit) {
      <div class="mt-4">
        <vw-premium-teaser
          [message]="'premium.teaser.' + structureLimitHit | translate"
          [detail]="'premium.teaser.' + structureLimitHit + '-detail' | translate"
          [source]="'garden_' + structureLimitHit"
          (ctaClicked)="openLimitPaywall()"
        ></vw-premium-teaser>
      </div>
    }
  </div>
}

<vw-premium-toast
  [isOpen]="premiumPaywallOpen"
  [source]="paywallSource"
  (close)="premiumPaywallOpen = false"
></vw-premium-toast>

<!-- Panneau « Ajouter une zone » : wizard 2 étapes (la zone → ses planches), slide-in.
     Ouvert, il n'a PAS de transform (transform-none, pas translate-x-0) : un transform en
     ferait le repère des feuilles « fixed » de son contenu, et la feuille Premium fermée du
     formulaire allongerait le défilement d'une bande blanche sous la barre « Continuer ». -->
<div
  class="fixed inset-0 z-[1000] overflow-y-auto bg-white transition-transform duration-200 ease-in-out"
  [ngClass]="showZoneForm ? 'transform-none' : 'pointer-events-none translate-x-full'"
>
  @if (showZoneForm) {
    <!-- La bande du haut est posée par l'en-tête de l'assistant. -->
    <vw-zone-create-wizard (finished)="onZoneFormClosed($event)"></vw-zone-create-wizard>
  }
</div>

<!-- Édition d'une zone (tap sur l'en-tête) : formulaire existant + action « Supprimer la zone ».
     Même règle que le panneau d'ajout : pas de transform ouvert, pas de marge sous le formulaire
     (sa barre « Enregistrer » colle au bas de l'écran et porte la zone sûre). -->
<div
  class="fixed inset-0 z-[1000] overflow-y-auto bg-white transition-transform duration-200 ease-in-out"
  [ngClass]="showZoneEdit ? 'transform-none' : 'pointer-events-none translate-x-full'"
>
  @if (showZoneEdit) {
    <vw-header-padding-safe-area [look]="'white'"></vw-header-padding-safe-area>
    <div class="px-4 pt-4">
      <vw-user-adress-location-form
        [location]="editZone"
        [showDelete]="true"
        (closeLocationForm)="onZoneEditClosed($event)"
        (deleteLocation)="onZoneDeleteRequest()"
      ></vw-user-adress-location-form>
    </div>
  }
</div>

<!-- Confirmation de suppression de zone (archive ses cultures, conserve l'historique). -->
<vw-confirm-dialog
  [isOpen]="showZoneDeleteConfirm"
  [title]="'garden.overview.delete-zone.title' | translate"
  [message]="'garden.overview.delete-zone.message' | vwPlural: zoneDeleteCount"
  [confirmLabel]="'garden.overview.delete-zone.confirm' | translate"
  [loading]="deletingZone"
  [error]="zoneDeleteFailed ? ('errors.businessException' | translate) : ''"
  (confirmed)="confirmZoneDelete()"
  (cancelled)="cancelZoneDelete()"
></vw-confirm-dialog>
